<template>
    <div
        ref="pageDetails"
        class="page-details"
        tabindex="0"
        @focusin="onFocus"
        @focusout="onFocusout"
    >
        <header-cast-detail />
        <div class="page-details-cast__lines">
            <lines-cast-detail 
                ref="linesCastDetail" 
                @ready="readyLines"
                @list-item-selected="itemsSelected"
                @focus-to-catcher="focusToCatcher"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import LinesCastDetail from "@/components/widgets/LinesCastDetail/LinesCastDetail.vue";
    import {PlayItem} from "@ca.vmedia.tv/domains";
    import type { LinesCastDetailRef } from "@/components/widgets/LinesCastDetail/types";
    import { useAccountStore } from "@/stores/Account";
    import { useParentControlStore } from "@/stores/ParentControl";
    import {AppConstants} from "@/common";
    import { useApplicationStore } from "@/stores/Application";
    import type {IEventSelected } from "@/components/RowView/types";
    import type {RelatedOnDemandCardData, CreditCardData} from "@/components/Cards/types";
    import { useDetailPageStore } from "@/stores/DetailPage";
    import {linkItems} from "@/components/Header/types";
    import { Strings } from "@/locales/types/enums/strings";
    import { useCastPageStore } from "@/stores/CastPage";
   
    const {
        setViewedCastDetailItems
    } = useCastPageStore();

    const DetailPageStoreModule = useDetailPageStore();
    const {
        setViewedDetailItems
    } = DetailPageStoreModule;

    const ApplicationStore = useApplicationStore();
    const {
        getIsHaveFocusedElement: isHavSelectedIdProgram,
    } = storeToRefs(ApplicationStore);
    const {
        setTooltipeShowing,
        setTooltipeMessage
    } = ApplicationStore;

    const ParentControlStoreModule = useParentControlStore();
    const {
        getIsModalParentVisible: isTypeModeEnabledPin
    } = storeToRefs(ParentControlStoreModule);

    const AccountStoreModule = useAccountStore()
    const {
        getForgotModalVisible: isForgotModalVisible
    } = storeToRefs(AccountStoreModule);

    definePageMeta({ 
        layout: 'default-no-header',
        middleware: ['set-page-indicator', 'fetch-detail-cast'],
    });

    const { t } = useI18n();
    const { setIsHaveFocusedElement, focusElementLine } = useFocus();
    const router = useRouter();
    const {$navigator } = useNuxtApp();
    const isRegKeyBackListeners: Ref<boolean> = ref(false);
    const pageDetails = ref<HTMLElement | null>(null);
    const linesCastDetail: Ref<LinesCastDetailRef | null> = ref(null);
    const isReadyLines: Ref<boolean> = ref(false);
    const isLoadedComponents = computed((): boolean => linesCastDetail.value !== null);
    const isReadyAll = computed((): boolean => isReadyLines.value && isLoadedComponents.value);
    const allowRegistrationKeyListeners = computed((): boolean => !isTypeModeEnabledPin.value && !isForgotModalVisible.value);
    const readyLines = () => isReadyLines.value = true;

    const onFocus = () => {
        registerKeyListeners();
    };
    const onFocusout = () => {
        unregisterKeyListeners();
    };

    const registerKeyListeners = () => {
        if (!isRegKeyBackListeners.value) {
            $navigator.on("keypress:back", keyBackListener);
            isRegKeyBackListeners.value = true;
        }
    };
    const unregisterKeyListeners = () => {
        if (isRegKeyBackListeners.value) {
            $navigator.off("keypress:back", keyBackListener);
            isRegKeyBackListeners.value = false;
        }
    };
    const keyBackListener = () => {
        router.go(-1);
    };
    const focusFirstRow = () => {
        if (linesCastDetail.value) {
            linesCastDetail.value.focusFirstRow();
        }
    };
    const focusToCatcher = () => {
        if (pageDetails.value) {
            pageDetails.value.focus();
        }
    };
    const focusElement = () => {
        if (isHavSelectedIdProgram.value) {
            focusElementLine();
        } else {
            focusFirstRow();
        }
    };
    const initFocus = (): void => {
        setIsHaveFocusedElement();
        focusElement();
    };

    const onRelatedSelected = (onDemandItem: RelatedOnDemandCardData) => {
        try {
            const playItem = onDemandItem.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(onDemandItem));
            }
            setViewedDetailItems(playItem);

            const [ id ] = onDemandItem.getId().split('_');
            
            const path = PlayItem.isSeries(playItem) ?
                `${linkItems.detailsVodSeries.routerPath}/${id}` :
                `${linkItems.detailsVodMovies.routerPath}/${id}`;

            router.push(path);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true, showingTime: 5000});
        }
    };

    const onCastSelected = (credit: CreditCardData) => {
        try {
            const creditItem = credit.getCreditItem();
            if (!creditItem) {
                throw new Error("СreditItem is null:" + JSON.stringify(credit));
            }

            const personId = credit.getPersonId();
            if (!personId) {
                throw new Error("The person ID is null:" + JSON.stringify(credit));
            }

            setViewedCastDetailItems(creditItem);

            router.push(`${linkItems.detailCast.routerPath}/${personId}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true, showingTime: 5000});
        }
    };
    const itemsSelected = (val: IEventSelected) => {
        switch (val.lineId) {
            case AppConstants.shelfKeys.castCards:
                onCastSelected(val.item as CreditCardData);
                break; 
            default:
                 onRelatedSelected(val.item as RelatedOnDemandCardData);
                break; 
        }
    };

    const handlerReady = (): void => {
        nextTick(() => {
            initFocus();
        });
    };

    watch(
        isReadyAll,
        (value: boolean) => {
            if (value) {
                handlerReady();
            }
        }
    );

    watch(
        allowRegistrationKeyListeners,
        (value: boolean) => {
            if (value) {
                initFocus();
            } else {
                unregisterKeyListeners();
            }
        }
    );

    onMounted(() => {
        nextTick(() => {
            registerKeyListeners();
        });
    });

    onBeforeUnmount(() => {
        unregisterKeyListeners();
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>